import { DeleteDocument } from "./DeleteDocument";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useRef, type ReactNode } from "react";
import { COMMERCIAL_DOCUMENT_LABELS } from "../../shared/commercial-documents";
import type { CustomerDocument } from "../../shared/customer-documents";
import type { OpsCustomerSummary } from "../../shared/operations-schemas";
import type { ToolHub } from "../../shared/tool-hubs";
import { Button, buttonVariants } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Sheet, SheetBody, SheetField, SheetFooter, SheetHeader } from "../components/ui/sheet";
import { documentDate } from "../knowledge/customer-document-format";
import { CustomerDocumentPanel, type DocumentPanelHandle } from "./CustomerDocumentDialog";
import {
  documentViewUrl,
  hubName,
  libraryRow,
  type DocumentAbilities as Can,
  type DocumentDialog as Dialog,
  type DocumentRow,
} from "./customer-document-rows";
import { dayOf } from "./customer-page-format";
import type { OperationsNavigate } from "./navigation";
import { linkDocumentToHub, linkLibraryDocumentToHub } from "./tool-hubs-api";

type PanelContext = {
  customer: OpsCustomerSummary;
  can: Can;
  hubs: readonly ToolHub[];
  navigate: OperationsNavigate;
};

/**
 * ADR-0087: the Documents tab has one side sheet. A contract shows its facts; its signed copy,
 * a library document, or a new one replaces that content in place, never a dialog on top.
 */
export function DocumentsPanel({
  context,
  row,
  dialog,
  hubIdOf,
  onDialog,
  onClose,
}: {
  context: PanelContext;
  row: DocumentRow | null;
  dialog: Dialog;
  hubIdOf: (documentId: string) => string | null;
  onDialog: (dialog: Dialog) => void;
  onClose: () => void;
}) {
  const panel = useRef<DocumentPanelHandle>(null);
  return (
    <Sheet
      open={row !== null || dialog !== null}
      onClose={() => {
        if (dialog && panel.current) panel.current.close();
        else onClose();
      }}
    >
      {dialog ? (
        <LibraryPanel
          key={dialog.document?.id ?? `new-${dialog.kind}`}
          panel={panel}
          context={context}
          dialog={dialog}
          hubId={dialog.document ? hubIdOf(dialog.document.id) : null}
          back={
            dialog.fromRow && row
              ? {
                  label: row.title,
                  onBack: () => {
                    onDialog(null);
                  },
                }
              : undefined
          }
          onClose={onClose}
        />
      ) : row?.source.kind === "generated" ? (
        <ContractPanel
          context={context}
          onClose={onClose}
          row={row}
          onSignedCopy={(copy) => {
            onDialog({ kind: copy.draft.fields.kind, document: copy, fromRow: row.key });
          }}
        />
      ) : (
        <>
          <SheetHeader title="" />
          <SheetBody />
          <SheetFooter />
        </>
      )}
    </Sheet>
  );
}

function LibraryPanel({
  panel,
  context,
  dialog,
  hubId,
  back,
  onClose,
}: {
  panel: React.RefObject<DocumentPanelHandle | null>;
  context: PanelContext;
  dialog: NonNullable<Dialog>;
  hubId: string | null;
  back: { label: string; onBack: () => void } | undefined;
  onClose: () => void;
}) {
  const { customer, can } = context;
  const document = dialog.document;
  return (
    <CustomerDocumentPanel
      ref={panel}
      slug={customer.slug}
      customerName={customer.name}
      kind={dialog.kind}
      {...(document ? { initial: document } : {})}
      canWrite={dialog.kind === "knowledge" ? can.writeKnowledge : can.manageCommercial}
      canUsePages={can.usePages}
      onClose={onClose}
      {...(back ? { back } : {})}
      aside={
        document ? <LibraryFacts context={context} document={document} hubId={hubId} /> : undefined
      }
    />
  );
}

function ViewLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      className={buttonVariants({ variant: "secondary" })}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children} <ExternalLink />
    </a>
  );
}

/** The tool a library document belongs to. The reader below it opens the stored file itself. */
function LibraryFacts({
  context,
  document,
  hubId,
}: {
  context: PanelContext;
  document: CustomerDocument;
  hubId: string | null;
}) {
  const { customer, can, hubs } = context;
  const row = libraryRow(document, hubId);
  const canAssign =
    document.draft.fields.kind === "knowledge" ? can.writeKnowledge : can.manageCommercial;
  return (
    <SheetField label="Tool">
      {canAssign && can.readKnowledge ? (
        <ToolAssignment row={row} hubs={hubs} organizationId={customer.id} />
      ) : (
        hubName(hubs, hubId)
      )}
    </SheetField>
  );
}

function ContractPanel({
  onClose,
  context,
  row,
  onSignedCopy,
}: {
  context: PanelContext;
  row: DocumentRow;
  onSignedCopy: (copy: CustomerDocument) => void;
  onClose: () => void;
}) {
  const { customer, can, hubs, navigate } = context;
  if (row.source.kind !== "generated") return null;
  const { document, signedCopy } = row.source;
  const view = signedCopy ? documentViewUrl(customer.slug, signedCopy) : null;
  return (
    <>
      <SheetHeader title={row.title} kicker={row.detail} />
      <SheetBody>
        <SheetField label="Status">
          {document.filing
            ? `Signed ${documentDate(document.filing.signedOn)} · version ${document.filing.revision} is locked`
            : row.status.label}
        </SheetField>
        {signedCopy ? (
          <SheetField label="Signed copy">
            <span className="block">
              {signedCopy.draft.fields.title} ·{" "}
              {signedCopy.published ? "shared with the customer" : "team only"}
            </span>
            <Button
              variant="link"
              className="min-h-0 p-0"
              onClick={() => {
                onSignedCopy(signedCopy);
              }}
            >
              Details and sharing
            </Button>
          </SheetField>
        ) : null}
        <SheetField label="Tool">
          {can.manageCommercial && can.readKnowledge ? (
            <ToolAssignment row={row} hubs={hubs} organizationId={customer.id} />
          ) : (
            hubName(hubs, row.hubId)
          )}
        </SheetField>
        <SheetField label="Customer can see">
          {row.visible !== "No"
            ? row.visible
            : signedCopy
              ? `No · share the signed copy to give ${customer.name} access.`
              : "No · only our team sees it."}
        </SheetField>
        <SheetField label="Updated">{dayOf(row.updatedAt)}</SheetField>
        {can.manageCommercial ? (
          <DeleteDocument
            slug={customer.slug}
            source="commercial"
            id={document.id}
            revision={document.revision}
            title={document.title}
            onDeleted={onClose}
          />
        ) : null}
      </SheetBody>
      <SheetFooter start={view ? <ViewLink href={view}>View signed copy</ViewLink> : undefined}>
        <Button
          onClick={() => {
            navigate(`/customers/${customer.slug}/documents/${document.id}`);
          }}
        >
          Open {COMMERCIAL_DOCUMENT_LABELS[document.kind].toLowerCase()}
        </Button>
      </SheetFooter>
    </>
  );
}

function ToolAssignment({
  row,
  hubs,
  organizationId,
}: {
  row: DocumentRow;
  hubs: readonly ToolHub[];
  organizationId: string;
}) {
  const client = useQueryClient();
  const save = useMutation({
    mutationFn: (hubId: string | null) =>
      row.source.kind === "generated"
        ? linkDocumentToHub(organizationId, row.source.document.id, hubId)
        : linkLibraryDocumentToHub(organizationId, row.source.document.id, hubId),
    onSuccess: () => client.invalidateQueries({ queryKey: ["ops", "tool-hubs", organizationId] }),
  });
  return (
    <>
      <select
        aria-label="Tool"
        className="min-h-11 w-full rounded-md border border-input bg-card px-3 text-base"
        value={row.hubId ?? ""}
        disabled={save.isPending}
        onChange={(event) => {
          save.mutate(event.target.value || null);
        }}
      >
        <option value="">Whole company</option>
        {hubs.map((hub) => (
          <option key={hub.id} value={hub.id}>
            {hub.name}
          </option>
        ))}
      </select>
      {save.error ? <FieldError>{save.error.message}</FieldError> : null}
    </>
  );
}
